import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Flame, Heart } from "lucide-react";

/**
 * iter63z'''''' — Hall of Burn.
 *
 * All-time league-wide leaderboard widget that lives directly under the
 * Smack Stream. Shows the top 3 posters (by smack-message count) AND the
 * top 3 reactors (by reactions given) across the ENTIRE league lifetime,
 * regardless of tournament. Ties at the third place are kept (so a 3-way
 * tie at the boundary will list all three).
 *
 * Refreshes on `refreshPing` (bumped by the parent dashboard on every
 * `smack_added` / `smack_deleted` / `smack_reaction_changed` WS event) so
 * the rankings update live as the league trash-talks.
 *
 * Renders a compact 2-column block (Posters | Reactors). Hidden entirely
 * when there's nothing to show OR Smack Stream is disabled for this league
 * (the endpoint returns empty arrays in that case).
 */
const RANK_BG = ["bg-heat/25 text-heat", "bg-cyber/20 text-cyber", "bg-zinc-700 text-zinc-300"];

export default function HallOfBurn({ leagueId, refreshPing = 0 }) {
    const [data, setData] = useState({ posters: [], reactors: [] });

    const load = useCallback(async () => {
        try {
            const r = await api.getHallOfBurn(leagueId);
            setData({ posters: r.posters || [], reactors: r.reactors || [] });
        } catch {
            // Silent — non-critical widget
        }
    }, [leagueId]);

    useEffect(() => { load(); }, [load, refreshPing]);

    const hasAny = data.posters.length > 0 || data.reactors.length > 0;
    if (!hasAny) return null;

    return (
        <div className="brutal-card p-3 space-y-2.5" data-testid="hall-of-burn-card">
            <div className="flex items-center gap-2">
                <Flame className="w-3.5 h-3.5 text-heat" />
                <h3 className="font-display font-extrabold text-[11px] uppercase tracking-tight">Hall of Burn</h3>
                <span className="text-[9px] uppercase tracking-widest text-zinc-500">All-time · Rivalry</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Column
                    testid="hall-of-burn-posters"
                    title="Top Posters"
                    icon={Flame}
                    iconClass="text-heat"
                    entries={data.posters}
                    unitLabel="msg"
                />
                <Column
                    testid="hall-of-burn-reactors"
                    title="Top Reactors"
                    icon={Heart}
                    iconClass="text-cyber"
                    entries={data.reactors}
                    unitLabel="react"
                />
            </div>
        </div>
    );
}

function Column({ testid, title, icon: Icon, iconClass, entries, unitLabel }) {
    return (
        <div data-testid={testid} className="space-y-1">
            <div className="flex items-center gap-1.5">
                <Icon className={"w-3 h-3 shrink-0 " + iconClass} />
                <span className="text-[9px] uppercase font-bold tracking-widest text-zinc-400">{title}</span>
            </div>
            {entries.length === 0 ? (
                <p className="text-[10px] text-zinc-600 italic">No data yet.</p>
            ) : (
                <ol className="space-y-1">
                    {entries.map((e, idx) => {
                        const rankClass = RANK_BG[Math.min(idx, 2)];
                        return (
                            <li
                                key={e.participant_id}
                                data-testid={`hall-of-burn-${testid.includes("posters") ? "poster" : "reactor"}-${idx + 1}`}
                                className="flex items-center gap-1.5 text-[11px]"
                            >
                                <span className={"inline-flex items-center justify-center w-4 h-4 text-[9px] font-extrabold font-mono " + rankClass}>
                                    {idx + 1}
                                </span>
                                <span className="font-bold text-zinc-200 truncate flex-1">{e.username}</span>
                                <span className="text-[10px] text-zinc-500 font-mono shrink-0">
                                    {e.count} {unitLabel}{e.count === 1 ? "" : "s"}
                                </span>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}
